import * as React from "react";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";

import { customValidators } from "../../utils/validators";

import Modal from "./Modal";
import ServerErrorMessage from "../atoms/ServerErrorMessage";
import Button from "../atoms/Button";
import BackButton from "../atoms/BackButton";
import QuantityControl from "../atoms/QuantityControl";
import Pill from "../atoms/Pill";

import useTicketsForPathDI from "../../hooks/useTicketsForPath";
import useLoginDI from "../../hooks/useLogin";
import Accordion from "../molecules/Accordion";
import Config from "../../constants/Config";
import { useHistory } from "react-router-dom";

type PropsType = {
    tickets: Record<string, any>;
    onClose: () => void;
    heading: string;
    active: boolean;
    event_ticket_comment_label: React.ReactNode;
    status?: string;
    path: string;
    previewId?: string;
    useTicketsForPath?: typeof useTicketsForPathDI;
    useLogin?: typeof useLoginDI;
};

const collateFormInputValidation = (inputs: Record<string, boolean>) => {
    const memberEntryRegex = /(\d+)-(\d+)-(.*)/;
    const collatedInputs: Record<string, boolean[]> = {};

    inputs &&
        Object.entries(inputs).forEach(([key, value]) => {
            const regexResult = memberEntryRegex.exec(key);
            if (regexResult) {
                const ticketId = regexResult[1];
                const ticketIndex = regexResult[2];
                const name = regexResult[3];
                if (
                    typeof ticketId === "string" &&
                    typeof ticketIndex === "string" &&
                    typeof name === "string"
                ) {
                    const ticket = collatedInputs[ticketId] ?? [];

                    let indexedTicket: boolean = ticket[ticketIndex] ?? true;

                    indexedTicket = indexedTicket && value;

                    ticket[ticketIndex] = indexedTicket;
                    collatedInputs[ticketId] = ticket;
                }
            }
        });

    return collatedInputs;
};

const GetTicketsModal = ({
    path,
    previewId,
    useTicketsForPath = useTicketsForPathDI,
    useLogin = useLoginDI,
    ...props
}: PropsType) => {
    const {
        tickets,
        teamTicketFromCart,
        selectedTeamTicketId,
        selectedTeamTicketSize,
        additionalQuestions,
        quantities,
        getTotalQuantity,
        getQuantity,
        incrementQuantity,
        decrementQuantity,
        resetQuantities,
        addTicketsToCart,
        isAddingTickets,
        isAddingTicketsSuccess,
        addingTicketsError,
        commentLabel,
    } = useTicketsForPath(
        previewId
            ? { preview: true, page_id: previewId }
            : { preview: false, path }
    );
    const { loginWithRedirect } = useLogin();
    const history = useHistory();

    const [freeText, setFreeText] = React.useState("");

    const collateFormData = (formData: Record<string, string>) => {
        const memberEntryRegex = /(\d+)-(\d+)-(.*)/;
        const collatedFormData: Record<string, Record<string, string>[]> = {};

        formData &&
            Object.entries(formData).forEach(([key, value]) => {
                const regexResult = memberEntryRegex.exec(key);
                if (regexResult) {
                    const ticketId = regexResult[1];
                    const ticketIndex = regexResult[2];
                    const name = regexResult[3];
                    if (
                        typeof ticketId === "string" &&
                        typeof ticketIndex === "string" &&
                        typeof name === "string"
                    ) {
                        const ticket = collatedFormData[ticketId] ?? [];

                        const indexedTicket: Record<string, string> =
                            ticket[ticketIndex] ??
                            (selectedTeamTicketId?.toString() !== ticketId
                                ? { free_text: formData["free_text"] ?? "" }
                                : {});

                        indexedTicket[name] = value;

                        ticket[ticketIndex] = indexedTicket;
                        collatedFormData[ticketId] = ticket;
                    }
                } else {
                    if (key === "team_name" && value !== "") {
                        collatedFormData["team"] = [
                            {
                                team_name: value,
                                free_text: formData["free_text"] ?? "",
                            },
                        ];
                    }
                }
            });

        return collatedFormData;
    };

    const [formData, setFormData] = React.useState<
        ReturnType<typeof collateFormData>
    >(collateFormData({}));

    const [accordionStatus, setAccordionStatus] = React.useState<
        Record<string, boolean[]>
    >({});

    const scrollToTop = () => {
        const ticketModalScroller = document.querySelector(
            ".get-tickets-modal .modal__content"
        );
        if (ticketModalScroller) {
            ticketModalScroller.scrollTo({
                top: 0,
                left: 0,
                // behavior: "smooth",
            });
        }
    };

    const additionalDetailsRequired =
        selectedTeamTicketSize != null || additionalQuestions?.length;

    type QuestionDetail = {
        name: string;
        label: string;
        info?: string;
        required?: boolean;
    } & (
        | {
              type: "textarea";
          }
        | { type: "radio"; options: { label: string; value: string }[] }
    );

    const detailsForAdditionalQuestions = (
        question: string
    ): QuestionDetail | undefined => {
        switch (question) {
            case "dietary-requirements":
                return {
                    name: question.replaceAll("-", "_"),
                    label: "Dietary requirements (Optional)",
                    info: "Please outline any dietary requirements.",
                    type: "textarea",
                };
            case "tshirt-size":
                return {
                    name: question.replaceAll("-", "_"),
                    label: "T-shirt size",
                    info: "Please specify your t-shirt size, XS, S, M, L, XL.",
                    type: "textarea",
                    required: true,
                };
            case "teammate-request":
                return {
                    name: question.replaceAll("-", "_"),
                    label: "Teammate request (Optional)",
                    info: "Is there a team mate you would like to request?",
                    type: "textarea",
                };
            case "accessibility-requirements":
                return {
                    name: question.replaceAll("-", "_"),
                    label: "Accessibility requirements (Optional)",
                    info: "Please outline any accessibility requirements.",
                    type: "textarea",
                };
            case "emergency-contact":
                return {
                    name: question.replaceAll("-", "_"),
                    label: "Emergency contact info",
                    info: "Name and contact number.",
                    type: "textarea",
                    required: true,
                };
            case "under-18":
                return {
                    name: question.replaceAll("-", "_"),
                    label: "Age",
                    type: "radio",
                    options: [
                        {
                            label: "18 years or over",
                            value: "18 years or over",
                        },
                        { label: "Under 18", value: "Under 18" },
                    ],
                    required: true,
                };
            default:
                return;
        }
    };

    const views: ["addTickets", ...string[]] = additionalDetailsRequired
        ? ["addTickets", "collectAdditionalDetails"]
        : ["addTickets"];
    const [currentView, setCurrentView] = React.useState<typeof views[number]>(
        views[0]
    );
    const incrementView = () => {
        scrollToTop();
        setAccordionStatus({});
        const nextView = views[views.indexOf(currentView) + 1];
        if (nextView != null) {
            setCurrentView(nextView);
        }
    };
    // const decrementView = () => {
    //     scrollToTop();
    //     const previousView = views[views.indexOf(currentView) - 1];
    //     if (previousView != null) {
    //         setCurrentView(previousView);
    //     }
    // };
    const isFinalView = () => views.length === views.indexOf(currentView) + 1;

    const handleFormSubmitted = async (...args) => {
        if (!isFinalView()) {
            // move to next form step
            incrementView();
            setFreeText(args[0]["free_text"]);
        } else {
            // submit/add to cart

            const ticketData = {
                ...Object.fromEntries(
                    Object.entries(quantities).map(([ticketId, quantity]) => [
                        ticketId,
                        Array.from({ length: quantity }).map(() => ({
                            free_text: args[0].free_text ?? freeText,
                        })),
                    ])
                ),
                ...collateFormData({ free_text: freeText, ...args[0] }),
            };
            if (await addTicketsToCart(ticketData)) {
                setTimeout(() => {
                    history.push(Config.routes.cart);
                }, 1500);
            }
        }
    };

    const renderAddTicketsView = () => {
        return (
            <>
                <h3 className="get-tickets-modal__heading">
                    {props.heading} tickets
                </h3>
                <div className="get-tickets-modal__details">
                    <ReactForm
                        onSuccess={handleFormSubmitted}
                        validators={customValidators}
                    >
                        {tickets &&
                            tickets.map((ticket, index) => {
                                if (ticket.eligibility.for && ticket.price) {
                                    const ticketUnavailable =
                                        !ticket.isInStock ||
                                        !ticket.isUserEligible ||
                                        !ticket.isAvailableForSale;

                                    const ticketName = ticket.ticketName;

                                    return (
                                        <div
                                            key={index}
                                            className={`get-tickets-modal__details__row ${
                                                ticketUnavailable
                                                    ? "get-tickets-modal__details__row--disabled"
                                                    : ""
                                            }`}
                                        >
                                            <div className="get-tickets-modal__details__row__info">
                                                <div className="get-tickets-modal__details__position">
                                                    {ticketName}
                                                </div>

                                                <div className="get-tickets-modal__details__price">
                                                    <span>${ticket.price}</span>

                                                    {!ticket.isInStock ||
                                                    !ticket.isAvailableForSale ? (
                                                        <Pill
                                                            color="dark-grey"
                                                            inverted
                                                        >
                                                            Sold out
                                                        </Pill>
                                                    ) : null}
                                                </div>

                                                {!ticket.isUserEligible ? (
                                                    <div className="get-tickets-modal__details__unavailable">
                                                        <button
                                                            className="get-tickets-modal__details__unavailable button--link"
                                                            onClick={() => {
                                                                loginWithRedirect(
                                                                    document
                                                                        .location
                                                                        .pathname
                                                                );
                                                            }}
                                                        >
                                                            Log in
                                                        </button>{" "}
                                                        with a
                                                        {ticket.eligibleAccountLabel
                                                            ? ` ${ticket.eligibleAccountLabel} `
                                                            : " "}
                                                        account to access
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="get-tickets-modal__details__quantity">
                                                <QuantityControl
                                                    disabled={ticketUnavailable}
                                                    quantity={getQuantity(
                                                        ticket.ID
                                                    )}
                                                    min={0}
                                                    max={ticket.maxQuantity}
                                                    onIncrement={() =>
                                                        incrementQuantity(
                                                            ticket.ID
                                                        )
                                                    }
                                                    onDecrement={() =>
                                                        decrementQuantity(
                                                            ticket.ID
                                                        )
                                                    }
                                                />
                                            </div>

                                            {teamTicketFromCart?.id ===
                                            ticket.ID ? (
                                                <ServerErrorMessage>
                                                    You have this item in your
                                                    cart already, max 1 per
                                                    checkout.
                                                </ServerErrorMessage>
                                            ) : null}
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        {(additionalQuestions?.length ?? 0) === 0 ? (
                            <div className="get-tickets-modal__details__additional-input">
                                <div className="get-tickets-modal__details__additional-input__label">
                                    {commentLabel?.length
                                        ? commentLabel
                                        : "Comment"}
                                </div>
                                <ReactFormField
                                    type="textarea"
                                    name={`free_text`}
                                    validators={["maxLength1000"]}
                                />
                            </div>
                        ) : null}

                        <Button
                            type="submit"
                            styles={["green"]}
                            aria-label="Add to cart"
                            loading={isAddingTickets}
                            success={isAddingTicketsSuccess}
                            disabled={getTotalQuantity() === 0}
                        >
                            {!isFinalView() ? "Next" : "Add to cart"}
                        </Button>
                    </ReactForm>

                    <ServerErrorMessage>
                        {addingTicketsError}
                    </ServerErrorMessage>
                </div>
            </>
        );
    };

    const renderAdditionalQuestion = (
        ticketId: number,
        questionDetail: QuestionDetail,
        ticketIndex: number = 0
    ) => {
        const renderLabelAndInfo = (label: string, info?: string) => (
            <>
                <div className="get-tickets-modal__details__additional-input__label">
                    {label}
                </div>
                {info && (
                    <div className="get-tickets-modal__details__additional-input__info">
                        {info}
                    </div>
                )}
            </>
        );

        switch (questionDetail?.type) {
            case "textarea":
                return (
                    <div
                        key={`${ticketId}-${ticketIndex}-${questionDetail.name}`}
                        className="get-tickets-modal__details__additional-input"
                    >
                        {renderLabelAndInfo(
                            questionDetail.label,
                            questionDetail.info
                        )}
                        <ReactFormField
                            type="textarea"
                            name={`${ticketId}-${ticketIndex}-${questionDetail.name}`}
                            required={questionDetail.required}
                            validators={["maxLength500"]}
                        />
                    </div>
                );
            case "radio":
                return (
                    <div
                        key={`${ticketId}-${ticketIndex}-${questionDetail.name}`}
                        className="get-tickets-modal__details__additional-input"
                    >
                        {renderLabelAndInfo(
                            questionDetail.label,
                            questionDetail.info
                        )}
                        <div>
                            {questionDetail.options.map(option => (
                                <ReactFormField
                                    key={option.value}
                                    type="radio"
                                    name={`${ticketId}-${ticketIndex}-${questionDetail.name}`}
                                    value={option.value}
                                    label={option.label}
                                    required={questionDetail.required}
                                />
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderAllAdditionalQuestions = (
        ticketId: number,
        ticketIndex = 0
    ) => {
        return (
            <>
                {additionalQuestions
                    ?.map(detailsForAdditionalQuestions)
                    .map(questionDetail => {
                        return (
                            questionDetail &&
                            renderAdditionalQuestion(
                                ticketId,
                                questionDetail,
                                ticketIndex
                            )
                        );
                    })}
            </>
        );
    };

    const renderTicketAccordion = (
        ticketId: number,
        ticketLabel: string,
        ticketIndex: number,
        teamMemberDetails: boolean = false,
        subtitle: string = ""
    ) => {
        return (
            <Accordion
                key={`${ticketId}-${ticketIndex}`}
                small={true}
                title={`${ticketLabel} ${ticketIndex + 1}`}
                subtitle={subtitle.length > 0 ? subtitle : undefined}
                isOpen={accordionStatus[ticketId]?.[ticketIndex]}
                setIsOpen={() => {
                    const updatedAccordionStatus = { ...accordionStatus };
                    const currentAccordion = updatedAccordionStatus[ticketId];
                    const updatedSingleAccordion =
                        currentAccordion == null ? [] : [...currentAccordion];
                    updatedSingleAccordion[ticketIndex] =
                        !updatedSingleAccordion[ticketIndex];
                    updatedAccordionStatus[ticketId] = updatedSingleAccordion;
                    setAccordionStatus(updatedAccordionStatus);
                }}
            >
                {/* <div className="get-tickets-modal__details__title">{`${ticketLabel} ${
                    ticketIndex + 1
                }`}</div> */}
                {teamMemberDetails && (
                    <>
                        <ReactFormField
                            type="text"
                            name={`${ticketId}-${ticketIndex}-first_name`}
                            label="First name"
                            validators={["customname"]}
                            required={true}
                        />
                        <ReactFormField
                            type="text"
                            name={`${ticketId}-${ticketIndex}-last_name`}
                            label="Last name"
                            validators={["customname"]}
                            required={true}
                        />
                        <ReactFormField
                            type="text"
                            name={`${ticketId}-${ticketIndex}-email`}
                            label="Email"
                            validators={["email"]}
                            required={true}
                        />
                        <ReactFormField
                            className={`${
                                !tickets?.find(ticket => ticket.ID === ticketId)
                                    ?.isUtsIdRequired
                                    ? "display-none"
                                    : ""
                            }`}
                            type="text"
                            name={`${ticketId}-${ticketIndex}-uts_id`}
                            label="UTS ID"
                            required={
                                tickets?.find(ticket => ticket.ID === ticketId)
                                    ?.isUtsIdRequired
                            }
                            validators={["utsid"]}
                        />
                        <ReactFormField
                            type="select"
                            name={`${ticketId}-${ticketIndex}-gender`}
                            label="Gender"
                        >
                            <option></option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="Other">Other</option>
                        </ReactFormField>
                    </>
                )}
                {renderAllAdditionalQuestions(ticketId, ticketIndex)}
            </Accordion>
        );
    };

    const renderCollectAdditionalDetailsView = () => {
        return (
            <>
                <br />
                {/* <BackButton
                    className="login-profile-body__back"
                    onClick={() => decrementView()}
                >
                    <span className="text">Back</span>
                </BackButton> */}

                <h3 className="get-tickets-modal__heading">
                    {props.heading} tickets
                </h3>

                <div className="get-tickets-modal__details">
                    <ReactForm
                        onSuccess={handleFormSubmitted}
                        onUpdate={formData => {
                            setFormData(collateFormData(formData));
                        }}
                        onError={(_, inputs) => {
                            const collatedFormInputValidation =
                                collateFormInputValidation(
                                    Object.fromEntries(
                                        inputs.map(input => [
                                            input.name,
                                            input.valid,
                                        ])
                                    )
                                );

                            const updatedAccordionStatus = Object.fromEntries(
                                Object.entries(collatedFormInputValidation).map(
                                    ([ticketId, indexedTickets]) => {
                                        return [
                                            ticketId,
                                            indexedTickets.map(
                                                (valid, i) =>
                                                    !valid ||
                                                    (accordionStatus[
                                                        ticketId
                                                    ]?.[i] ??
                                                        false)
                                            ),
                                        ];
                                    }
                                )
                            );
                            setAccordionStatus(updatedAccordionStatus);
                            setTimeout(() => {
                                document
                                    .querySelector(
                                        `[name='${
                                            inputs.find(input => !input.valid)
                                                .name
                                        }']`
                                    )
                                    ?.parentElement?.parentElement?.scrollIntoView(
                                        { behavior: "smooth" }
                                    );
                            }, 0);
                        }}
                        validators={customValidators}
                    >
                        {getTotalQuantity() === 1 && !selectedTeamTicketId ? (
                            <>
                                <div className="get-tickets-modal__details__accordion__title">
                                    {}
                                    {(() => {
                                        const ticket = tickets?.find(
                                            ticket =>
                                                ticket.ID ===
                                                Number.parseInt(
                                                    Object.entries(
                                                        quantities
                                                    ).find(
                                                        ([_, quantity]) =>
                                                            quantity > 0
                                                    )?.[0]!
                                                )
                                        );
                                        return (
                                            ticket?.ticketName.match(
                                                /(.*) ticket/i
                                            )?.[1] ?? ticket?.ticketName
                                        );
                                    })()}
                                </div>
                                <div className="get-tickets-modal__details__single-ticket">
                                    {renderAllAdditionalQuestions(
                                        Number.parseInt(
                                            Object.entries(quantities).find(
                                                ([_, quantity]) => quantity > 0
                                            )?.[0]!
                                        )
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                {selectedTeamTicketId ? (
                                    <>
                                        <div className="get-tickets-modal__details__accordion__title">
                                            Team
                                        </div>
                                        <ReactFormField
                                            type="text"
                                            name="team_name"
                                            label="Team name"
                                            validators={["customname"]}
                                            required={true}
                                        />
                                        <div className="get-tickets-modal__details__accordion__container">
                                            {selectedTeamTicketSize
                                                ? Array.from({
                                                      length: selectedTeamTicketSize,
                                                  }).map((_, i) => {
                                                      const memberDatum =
                                                          selectedTeamTicketId &&
                                                          formData[
                                                              selectedTeamTicketId
                                                          ]?.[i];
                                                      const memberName = `${
                                                          memberDatum?.[
                                                              "first_name"
                                                          ]
                                                              ? `${memberDatum["first_name"]} `
                                                              : ""
                                                      }${
                                                          memberDatum?.[
                                                              "last_name"
                                                          ]
                                                              ? memberDatum[
                                                                    "last_name"
                                                                ]
                                                              : ""
                                                      }`;

                                                      return (
                                                          selectedTeamTicketId &&
                                                          renderTicketAccordion(
                                                              selectedTeamTicketId,
                                                              "Team member",
                                                              i,
                                                              true,
                                                              memberName
                                                          )
                                                      );
                                                  })
                                                : null}
                                        </div>
                                    </>
                                ) : null}

                                {(additionalQuestions?.length ?? 0) > 0
                                    ? Object.entries(quantities)
                                          .map(
                                              ([ticketId, quantity]) =>
                                                  [
                                                      Number.parseInt(ticketId),
                                                      quantity,
                                                  ] as [number, number]
                                          )
                                          .filter(
                                              ([ticketId, quantity]) =>
                                                  ticketId !==
                                                  selectedTeamTicketId
                                          )
                                          .map(([ticketId, quantity]) => {
                                              const ticket = tickets?.find(
                                                  ticket =>
                                                      ticket.ID === ticketId
                                              );
                                              return ticket && quantity ? (
                                                  <React.Fragment
                                                      key={ticket.ticketName}
                                                  >
                                                      {/* {Object.values(
                                                    quantities
                                                ).filter(
                                                    quantity => quantity > 0
                                                ).length >= 2 ? ( */}
                                                      <div className="get-tickets-modal__details__accordion__title">
                                                          {ticket.ticketName.match(
                                                              /(.*) ticket/i
                                                          )?.[1] ??
                                                              ticket.ticketName}
                                                      </div>
                                                      {/* ) : null} */}
                                                      <div className="get-tickets-modal__details__accordion__container">
                                                          {Array.from({
                                                              length: quantity,
                                                          }).map((_, i) =>
                                                              renderTicketAccordion(
                                                                  ticketId,
                                                                  ticket.ticketName,
                                                                  i
                                                              )
                                                          )}
                                                      </div>
                                                  </React.Fragment>
                                              ) : null;
                                          })
                                    : null}
                            </>
                        )}

                        {(additionalQuestions?.length ?? 0) > 0 ? (
                            <div className="get-tickets-modal__details__additional-input">
                                <div className="get-tickets-modal__details__additional-input__label">
                                    {commentLabel?.length
                                        ? commentLabel
                                        : "Comment"}
                                </div>
                                <ReactFormField
                                    type="textarea"
                                    name={`free_text`}
                                    validators={["maxLength1000"]}
                                />
                            </div>
                        ) : null}

                        <Button
                            type="submit"
                            styles={["green"]}
                            aria-label="Add to cart"
                            loading={isAddingTickets}
                            success={isAddingTicketsSuccess}
                            successText="Added to cart"
                        >
                            {"Add to cart"}
                        </Button>
                    </ReactForm>

                    <ServerErrorMessage>
                        {addingTicketsError}
                    </ServerErrorMessage>
                </div>
            </>
        );
    };

    return (
        <Modal
            aside={true}
            active={props.active}
            className="get-tickets-modal"
            onClose={props.onClose}
            onOverlayClick={props.onClose}
            onExited={() => {
                resetQuantities();
                setCurrentView("addTickets");
            }}
        >
            <Modal.Header>Get tickets</Modal.Header>
            <Modal.Content>
                <div className="container-fluid get-tickets-modal__content__inner">
                    {currentView === "addTickets" && renderAddTicketsView()}
                    {currentView === "collectAdditionalDetails" &&
                        renderCollectAdditionalDetailsView()}
                </div>
            </Modal.Content>
        </Modal>
    );
};

export default GetTicketsModal;
